import React, { useState, useEffect } from "react";
import { TopMenuBar } from "../components/TopMenuBar/TopMenuBar";
import PackoutNameForm from "../components/PackoutNameForm/PackoutNameForm";
import { alpha, styled } from "@mui/material/styles";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ProgressBar from "../components/ProgressBar/ProgressBar";
import NavBar from "../components/NavBar/NavBar";
import Switch from "@mui/material/Switch";
import PostAddIcon from "@mui/icons-material/PostAdd";
import DeleteIcon from '@mui/icons-material/Delete';
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import { api } from "../TokenManager";
import "./style.css";
import * as Utils from "../Utils";
import { saveExcelDataForEditing } from '../parser/Parser';
import "@fontsource-variable/open-sans";

function timeout(delay) {
  return new Promise((res) => setTimeout(res, delay));
}

// 1. Updated waitHandler signature to accept sessionId
async function waitHandler(allFiles, packoutName, newRunId) {
  if (!allFiles || allFiles.length === 0) {
    console.error("No files provided");
    return;
  }

  localStorage.setItem('lastLoadOut', allFiles.length);

  try {
    // Separate files while retaining their absolute order/indexes
    const csvFiles = [];
    const excelFiles = [];

    allFiles.forEach((file, index) => {
      const ext = file.name.split('.').pop().toLowerCase();
      const isCsv = ext === 'csv' || file.type === 'text/csv';
      const fileWithOrder = { file, order: index + 1 };

      if (isCsv) {
        csvFiles.push(fileWithOrder);
      } else {
        excelFiles.push(fileWithOrder);
      }
    });

    // Process Excel files if present
    if (excelFiles.length > 0) {
      console.log("Processing Excel file(s)...");
      const primaryExcel = excelFiles[0].file;
      const secondaryExcels = excelFiles.slice(1).map(item => item.file);
      // Map absolute load orders for the excel files
      const excelOrders = excelFiles.map(item => item.order);

      await saveExcelDataForEditing(primaryExcel, secondaryExcels, packoutName, newRunId, excelOrders);
    }

    // Process CSV files if present
    if (csvFiles.length > 0) {
      console.log("Processing CSV file(s)...");
      const primaryCsv = csvFiles[0].file;
      const secondaryCsvs = csvFiles.slice(1).map(item => item.file);
      const initialCsvLoadOrder = csvFiles[0].order;

      await Utils.saveCSVDataForEditing(
        primaryCsv, 
        secondaryCsvs, 
        packoutName, 
        newRunId, 
        null, 
        initialCsvLoadOrder
      );
    }

    await timeout(1000);
    window.location.href = "#/checklistPage";

  } catch (error) {
    console.error("An error occurred during file processing:", error);
  }
}

const ColoredSwitch = styled(Switch)(({ theme }) => ({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#0071ba",
    "&:hover": {
      backgroundColor: alpha("#0071ba", theme.palette.action.hoverOpacity),
    },
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#0071ba",
  },
}));

export const UploadFilePage = (props) => {
  const { isImperialprop, onUnitChange } = props;
  const [allFiles, setAllFiles] = useState([]);
  const [allFileNames, setAllFileNames] = useState([]);
  const [isImperial, setIsImperial] = useState(isImperialprop);
  const [showAddAdditonalFiles, setShowAddAdditonalFiles] = useState(false);
  const [alert, setAlert] = useState(false);
  const [alertContent, setAlertContent] = useState("");
  const [packoutName, setPackoutName] = useState(
    () => localStorage.getItem("packoutname") || "Packout"
  );

  const handleOnChooseFile = (e) => {
    if (e.target.files[0]) {
      setAllFiles((prevFiles) => [...prevFiles, e.target.files[0]]);
      setAllFileNames((prevFiles) => [...prevFiles, e.target.files[0].name]);
    } else {
      setAlert(true);
    }
    setShowAddAdditonalFiles(true);
    setAlert(false);
  };

  const handleDeleteFile = (index) => {
    setAllFileNames((prev) => prev.filter((_, i) => i !== index));
    setAllFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOnSwitch = () => {
    if (isImperial === true) {
      setIsImperial(false);
      onUnitChange(false);
      console.log("changing to metric units");
    } else {
      setIsImperial(true);
      onUnitChange(true);
    }
  };

  function showList() {
    if (allFiles.length > 0) {
      return (
        <div className="list-scroll" >
          {allFileNames.map((name, index) => (
            <div
              className="uploaded-files"
              key={index}
            >
              <PostAddIcon className="icons-8" />
              <p style={{ margin: 0 }}>{name}</p>
              <DeleteIcon
                className="delete-icon"
                onClick={() => handleDeleteFile(index)}
                aria-label={`Delete ${name}`}
              />
            </div>
          ))}
        </div>
      );
    }
  }

  function addAdditionalFiles() {
    if (showAddAdditonalFiles) {
      return showList(0);
    } else {
      return;
    }
  }

  const handleOnNextStep = async (e) => {
  e.preventDefault();

  if (!allFiles[0]) {
    setAlert(true);
    setAlertContent("No file uploaded");
    return;
  }

  const normalizedPackoutName = String(packoutName || "").trim();

  if (!normalizedPackoutName) {
    setAlert(true);
    setAlertContent("Packout name is required");
    return;
  }

  try {
    const response = await api.post("/api/packout-runs", {
      packoutName: normalizedPackoutName,
    });

    const newRunId = response.data.run.runId || response.data.run.id;

    if (!newRunId) {
      throw new Error("The backend did not return a runId.");
    }

    console.log("New Run Context Initialized:", newRunId);

    // runId is an object identifier, not a credential. The backend still
    // validates ownership for every run-scoped API call.
    Utils.setCurrentRunId(newRunId);

    await waitHandler(allFiles, normalizedPackoutName, newRunId);
  } catch (error) {
    console.error("Failed to transition to new run context:", error);

    setAlert(true);

    const message =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      "Could not start a new run configuration.";

    setAlertContent(message);
  }
};

  return (
    <div className="upload-file">
      <TopMenuBar />
      <div className="under-banner">
        <ProgressBar page="upload"></ProgressBar>
        <div className="div">
          <div className="upload-image-default">
            <div className="adicionar-novo-job">
              <div className="name-form">
                <div className="left-side">
                  <PackoutNameForm name={packoutName} setName={setPackoutName} />
                  <p className="directions">
                    <span className="p-bold">
                      1. To begin, upload the file that contains your packing
                      data.
                    </span>{" "}
                    Make sure the file is in .CSV or .XLSX format — this is a common type
                    of spreadsheet file.
                  </p>
                </div>
              </div>
              <div className="uploadButton-and-files-container">
                <Button
                  aria-label="home"
                  className="upload-button"
                  component="label"
                >
                  <div className="card-add-product">
                    {!showAddAdditonalFiles && (
                      <div className="upload-instructions">
                        <UploadFileIcon className="icons-7" />
                        <p className="p">Click here to upload a CSV or R34 Excel file</p>
                        {alert ? (
                          <Alert severity="error">{alertContent}</Alert>
                        ) : (
                          <></>
                        )}
                      </div>
                    )}

                    {showAddAdditonalFiles && (
                      <div className="upload-instructions">
                        <PostAddIcon className="icons-7" />
                        <p className="p">
                          Click here to upload an additional CSV or R34 Excel file to add to an
                          existing packed order
                        </p>
                      </div>
                    )}
                    <input
                      type={"file"}
                      id={"csvFileInput"}
                      accept={".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .xlsx"}
                      onChange={handleOnChooseFile}
                      hidden
                    />
                  </div>
                </Button>
                {addAdditionalFiles()}
              </div>

              <div className="metricswitch">
                Metric
                <ColoredSwitch
                  checked={isImperial}
                  onChange={handleOnSwitch}
                />{" "}
                Imperial
              </div>
            </div>
          </div>
          <NavBar>
            <button className="button-wrapper">
              <Button href="/" className="button-6">
                Back
              </Button>
            </button>
            <button className="div-wrapper">
              <Button className="button-7" onClick={handleOnNextStep}>
                Next Step
              </Button>
            </button>
          </NavBar>
        </div>
      </div>
    </div>
  );
};

export default UploadFilePage;
